import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IconButtonBox from '../../components/IconButtonBox';
import ButtonBox from '../../components/ButtonBox';
import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import { APPLICATION_ID, ROUTE_APPLICATION_CREATE, ROUTE_APPLICATION_INFORMATION } from '../../constants';
import useApplicationStore from '../../stores/ApplicationStore';
import NewTableBox, { RowData } from '../../components/NewTableBox';
import ApplicationApi from '../../api/ApplicationApi';
import { addLocalStorage, removeLocalStorage, useError } from '../../utils/functions';
import DialogBox from '../../components/DialogBox';
import usePaginationStore from '../../stores/PaginationStore';

const Application: React.FC = () => {
    const { setError } = useError();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { setCreateMode } = useApplicationStore();
    const [delOpen, setDelOpen] = useState(false);

    const [totalCount, setTotalCount] = useState(0);
    const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

    const [formValues, setFormValues] = useState<{
        delName: string;
        delObj: any | null;
    }>({
        delName: '',
        delObj: null,
    });


    const handleChange = (field: string, value: any) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    };

    const handleCreate = () => {
        setCreateMode(true);
        navigate(ROUTE_APPLICATION_CREATE);
    }

    const handleSetEdit = (row: RowData) => {
        addLocalStorage(APPLICATION_ID, row.id);
        navigate(ROUTE_APPLICATION_INFORMATION);
    };

    const columns = [
        { field: "name", title: "Name", isSearch: true, isLink: true, linkHandler: (val: any, row: RowData) => { handleSetEdit(row) } },
        { field: "description", title: "Description", isSearch: true },
        { field: "createdAt", title: "Created at", isSort: true },
    ];

    const openDelete = (rowData: any) => {
        setDelOpen(true);
        handleChange('delName', rowData.name);
        handleChange('delObj', rowData);
    };

    const handleDelete = async () => {
        const rowData = formValues.delObj;
        if (rowData) {
            try {
                await ApplicationApi.deleteApp(rowData.id);
                if (rowData.redirectURIs) {
                    for (let redirect of rowData.redirectURIs) {
                        await ApplicationApi.deleteRedirectUri(rowData.id, redirect.id);
                    }
                }

                if (rowData.projects) {
                    for (let project of rowData.projects) {
                        await ApplicationApi.deleteProjectAccess(rowData.id, project.id);
                    }
                }

                setDelOpen(false);
                handleChange('delName', '');
                handleChange('delObj', null);
                setReQuery(true);
                queryApplication();
            } catch (error) {
                console.error("Error handleDelete:", error);
            }
        }
    };

    const queryApplication = async () => {
        setData([]);
        try {
            const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();

            const paginationParams = {
                limit: limit,
                beforeCursor: beforeCursor,
                afterCursor: afterCursor,
            };

            const appsResponse = await ApplicationApi.getApps(paginationParams);

            if (appsResponse.code) {
                setError(appsResponse.message);
            } else {
                setData(appsResponse?.data);

                if (appsResponse.metadata && appsResponse.metadata.total) {
                    setTotalCount(appsResponse.metadata.total);
                }

                if (appsResponse.paging) {
                    setAfterCursor(appsResponse.paging.afterCursor);
                    setBeforeCursor(appsResponse.paging.beforeCursor);
                }
            }

        } catch (error) {
            console.error("Error fetching appsResponse data:", error);
        } finally {
            closeAction();
        }
    }

    useEffect(() => {
        removeLocalStorage(APPLICATION_ID);
        resetStore();
        queryApplication();
    }, []);

    useEffect(() => {
        if (pageAction) {
            queryApplication();
        }
    }, [pageAction]);


    return (
        <>
            <Typography className="layout-title">
                Applications
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box />
                <Box>
                    <IconButtonBox
                        icon={<CachedIcon />}
                        text='Refresh'
                        onClick={queryApplication} />
                    <ButtonBox
                        text='Create new'
                        onClick={handleCreate}
                        icon={<AddIcon />} />
                </Box>
            </Box>
            <NewTableBox columns={columns} data={data} showDelete={true} deleteFunction={(rowData) => openDelete(rowData)} totalCount={totalCount} />
            <DialogBox
                open={delOpen}
                title='Delete'
                dialogContent={
                    <>
                        {formValues.delName} will be permanently deleted.
                    </>
                }
                showCancel={true}
                onCancel={() => setDelOpen(false)}
                onConfirm={handleDelete}
                isError={true}
                confirmLabel='Delete'
            />
        </>
    );
};

export default Application;
